import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localePt from '@angular/common/locales/pt';
import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  isDevMode,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideSignalFormsConfig } from '@angular/forms/signals';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { LANGUAGES, provideLanguage } from '@nbx/core/language';
import { PRIMENG_LICENSE, createRentsodyPreset } from '@nbx/styles/prime-ng';

import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';

import { TranslocoHttpLoader } from '@core/services/transloco-loader';
import { HTTP_INTERCEPTORS } from '@interceptors/interceptors';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';

import { routes } from './app.routes';

registerLocaleData(localeEs, 'es');
registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localePt, 'pt');

const AVAILABLE_LANGUAGES = ['es', 'en', 'de', 'pt'];

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAnimationsAsync(),
    provideSignalFormsConfig({
      classes: {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- CSS class name, applied to [formField] hosts (native PrimeNG inputs) once the field is invalid and touched.
        'p-invalid': field => field.state().invalid() && field.state().touched(),
      },
    }),
    provideRouter(
      routes,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      withComponentInputBinding(),
      withViewTransitions()
    ),
    provideHttpClient(withXhr(), withInterceptors(HTTP_INTERCEPTORS)),
    provideLanguage(),
    { provide: LANGUAGES, useValue: AVAILABLE_LANGUAGES },
    { provide: LOCALE_ID, useFactory: () => inject(TranslocoService).getActiveLang() },
    MessageService,
    providePrimeNG({
      license: PRIMENG_LICENSE,
      theme: {
        preset: createRentsodyPreset(),
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: {
            name: 'primeng',
            order: 'base, primeng, theme, typography, components',
          },
        },
      },
      zIndex: {
        modal: 1100,
        overlay: 1300,
        menu: 1300,
        tooltip: 1400,
      },
    }),
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGUAGES,
        defaultLang: 'es',
        fallbackLang: 'es',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
          logMissingKey: isDevMode(),
        },
      },
      loader: TranslocoHttpLoader,
    }),
  ],
};
